import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSwipeable } from "react-swipeable";

import EllipsisLoader from "../components/EllipsisLoader";
import Icon from "../components/Icon";
import StyleHeroBanner from "../components/StyleHeroBanner";
import { ApiContext, NavigationContext, UserContext } from "../contexts";
import useWindowSize from "../hooks/useWindowSize";
import { getPageTitle } from "../resources/routes";
import {
  ExteriorStyle,
  HouseType,
  IconName,
  LandType,
  UpgradeStyle,
} from "../types";
import { scrollToTop } from "../util";

const ChooseExteriorStyleScreen = observer(() => {
  const apiContext = useContext(ApiContext);
  const userContext = useContext(UserContext);
  const navContext = useContext(NavigationContext);
  const location = useLocation();

  const windowSize = useWindowSize();
  const [activePage, setActivePage] = useState(1);
  const [perPage, setPerPage] = useState(8);

  const exteriorStyles = apiContext.exteriorStyleResults;

  useEffect(() => {
    scrollToTop();
    navContext.setFooterModePrimary();
    navContext.setNextButtonEnabled(true);

    navContext.setOnNavigateBack(() => {
      // Reset selection when the user goes back
      userContext.setSelectedExteriorStyle(null);
      userContext.resetSelectedRoofStyle();
      userContext.resetSelectedSelectedGutterStyle();
      userContext.resetSelectedSelectedWindowStyle();
    });

    // Select default option
    if (userContext.selectedExteriorStyle) {
      onClickExteriorColour(userContext.selectedExteriorStyle);
    } else if (exteriorStyles.length) {
      onClickExteriorColour(exteriorStyles[0]);
    }

    return () => {
      navContext.resetBackBtnOnClick();
    };
  }, []);

  useEffect(() => {
    navContext.setSubtotalVisible(userContext.subtotalPrice > 0);
  }, [userContext.subtotalPrice]);

  const onClickExteriorColour = (exteriorStyle: ExteriorStyle) => {
    if (
      !userContext.selectedRoofStyle.has(exteriorStyle.id) &&
      exteriorStyle.roof_options.length > 0
    ) {
      // Make sure we always select a default if the user has never visited this exterior style before
      userContext.setSelectedRoofStyle(
        exteriorStyle.id,
        exteriorStyle.roof_options[0]
      );
    }

    if (
      !userContext.selectedGutterStyle.has(exteriorStyle.id) &&
      exteriorStyle.gutter_options.length > 0
    ) {
      // Make sure we always select a default if the user has never visited this exterior style before
      userContext.setSelectedGutterStyle(
        exteriorStyle.id,
        exteriorStyle.gutter_options[0]
      );
    }

    if (
      !userContext.selectedWindowStyle.has(exteriorStyle.id) &&
      exteriorStyle.window_options.length > 0
    ) {
      // Make sure we always select a default if the user has never visited this exterior style before
      userContext.setSelectedWindowStyle(
        exteriorStyle.id,
        exteriorStyle.window_options[0]
      );
    }

    userContext.setSelectedExteriorStyle(exteriorStyle);
  };

  const onClickRoof = (option: UpgradeStyle) => {
    if (userContext.selectedExteriorStyle) {
      userContext.setSelectedRoofStyle(
        userContext.selectedExteriorStyle.id,
        option
      );
    }
  };

  useEffect(() => {
    if(windowSize.width > 991){
      setPerPage(8);
    }else if(windowSize.width > 767){
      setPerPage(6);
    }else{
      setPerPage(4);
    }
  }, [windowSize.width]);

  const renderTabContent = (tabIndex: number) => {
    const selectedExterior = userContext.selectedExteriorStyle;
    if (!selectedExterior) {
      return <></>;
    }
    const selectedRoofStyle = userContext.selectedRoofStyle.get(
      selectedExterior.id
    );

    const selectedGutterStyle = userContext.selectedGutterStyle.get(
      selectedExterior.id
    );

    const selectedWindowStyle = userContext.selectedWindowStyle.get(
      selectedExterior.id
    );

    let roofId = -1;
    let gutterId = -1;
    let windowId = -1;

    if (tabIndex === 1 && selectedRoofStyle) {
      roofId = selectedRoofStyle.id;
    } else if (tabIndex === 2 && selectedGutterStyle) {
      gutterId = selectedGutterStyle.id;
    } else if (tabIndex === 3 && selectedWindowStyle) {
      windowId = selectedWindowStyle.id;
    }

    return (
      <div className={"tab-content"}>
        <StyleHeroBanner
          showRendivoDisclaimer={apiContext.rendivoDisclaimerVisible}
          images={[userContext.rendivoExteriorRender]}
          usingRendivoImages={true}
          key={`${selectedExterior.id}_${roofId}_${gutterId}_${windowId}`}
          fancybox
        />
      </div>
    );
  };

  let houseType = HouseType.DOUBLE_STORY;
  let lotWidth = userContext.selectedHome
    ? userContext.selectedHome.lot_width
    : "";

  if (
    userContext.landType === LandType.HOUSE_LAND_PACKAGE &&
    userContext.selectedHouseLand
  ) {
    houseType =
      userContext.selectedHouseLand.rendivo_levels == 1
        ? HouseType.SINGLE_STORY
        : HouseType.DOUBLE_STORY;
    lotWidth = userContext.selectedHouseLand.lot_width;
  } else if (
    userContext.landType !== LandType.HOUSE_LAND_PACKAGE &&
    userContext.selectedFacade
  ) {
    houseType =
      userContext.selectedFacade.rendivo_levels == 1
        ? HouseType.SINGLE_STORY
        : HouseType.DOUBLE_STORY;
  }

  const onClickPrev = () => {
    if (canClickPrev()) {
      setActivePage(activePage - 1);
    }
  };
  const onClickNext = () => {
    if (canClickNext()) {
      setActivePage(activePage + 1);
    }
  };

  const canClickPrev = () => {
    return activePage > 1;
  };
  
  const canClickNext = () => {
    return activePage < Math.ceil(exteriorStyles.length / perPage);
  };

  const swipeHandlers = useSwipeable({
    delta: 10,
    preventDefaultTouchmoveEvent: false,
    trackTouch: true,
    trackMouse: true,
    rotationAngle: 0,
    onSwiped: (eventData) => {
      console.log(eventData);
      if (eventData.dir === "Left") {
        onClickNext();
      } else if (eventData.dir === "Right") {
        onClickPrev();
      }
    },
  });

  return (
    <div
      className={`screen screen-choose-exterior-style house-type-${houseType} lot-width-${lotWidth}`}
    >
      <div className={"container"}>
        <h2 className="h1"><strong className="number">Step 3:</strong> {getPageTitle(location.pathname)}</h2>
        <p className={"subtitle"}>
          Choose a colour scheme that reflects your very own taste and
          personality. Inspired by the natural landscape.
        </p>
      </div>
      <div className="flex">
        <div className="flex-left">
          {renderTabContent(1)}
        </div>
        <div className="flex-right">
          <div className="thumbnails-nav">
            <span className="instructions">Choose Your Facade Colour Scheme</span>
            <div className="nav-right">
              <span>{exteriorStyles.length} Options</span>
            </div>
          </div>
          <div className={"thumbnails"} {...swipeHandlers}>
            {exteriorStyles.map((exterior, index) => {
              return (
                <div
                  key={exterior.id}
                  className={`thumbnail ${userContext.selectedExteriorStyle && userContext.selectedExteriorStyle.id === exterior.id ? "active" : ""}`} 
                  onClick={() => onClickExteriorColour(exterior)}
                >
                  <div className="thumbnail-image">
                    <EllipsisLoader />
                    <img src={exterior.thumbnail} alt={""} />
                  </div>
                  <div className="thumbnail-text">{ exterior.title }</div>
                </div>
              );
            })}
          </div>
          {userContext.selectedExteriorStyle && (
            <div className="roofs">
              <div className="thumbnails-nav">
                <span className="instructions">Choose Your Roof Type</span>
                <div className="nav-right">
                  <span>{userContext.selectedExteriorStyle.roof_options.length} Options</span>
                </div>
              </div>
              <div className={"thumbnails"}>
                {userContext.selectedExteriorStyle.roof_options.map((roof) => {
                  const selectedRoof = userContext.selectedRoofStyle.get(userContext.selectedExteriorStyle ? userContext.selectedExteriorStyle.id : 0);
                  return (
                    <div
                      key={roof.id}
                      className={`thumbnail ${selectedRoof && selectedRoof.id === roof.id ? "active" : ""}`} 
                      onClick={() => onClickRoof(roof)}
                    >
                      <div className="thumbnail-image">
                        <EllipsisLoader />
                        <img src={roof.thumbnail} alt={""} />
                      </div>
                      <div className="thumbnail-text">{ roof.title }</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default ChooseExteriorStyleScreen;
